import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Download, Loader2 } from 'lucide-react';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { SnapshotChangePill } from '@/components/snapshot/SnapshotChangePill';
import { snapshotRangesValid, type SnapshotRangeState } from '@/components/snapshot/SnapshotDateRangePicker';
import { TableSearchInput } from '@/components/TableSearchInput';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { workspaceTableClass } from '@/components/workspace/WorkspaceSection';
import {
  useWorkspaceFrozenLayout,
  workspaceColumnStickyProps,
  WORKSPACE_MIN_COLUMN_WIDTH_PX,
} from '@/components/workspace/workspaceStickyColumns';
import {
  dateInRange,
  groupFlipkartSynopsisRows,
  summarizeFlipkartWindow,
} from '@/components/flipkart/flipkartSynopsisMetrics';
import { FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY, flipkartAdsReportsQueryKey } from '@/components/FlipkartAdsOauthConnections';
import { cn } from '@/utils/cn';
import { EMPTY_RETAIL_KPI_METRICS, type WorkspaceRetailKpiMetrics } from '@/utils/workspaceAdProductMetrics';
import { rollupSalesByAsin, rollupTrafficByAsin, clipRangeToVendorLag2 } from '@/utils/vendorRetailMetrics';
import {
  adsApi,
  flipkartAdsApi,
  spApiOauthApi,
  type FlipkartAdsProduct,
  type FlipkartAdsReportRow,
  type SpApiAsinRow,
  type SpApiSalesAsinRow,
  type SpApiTrafficRow,
} from '@/api/client';
import {
  applyPerformance,
  buildConsolidatedAsinRows,
  channelAcos,
  channelAov,
  channelCr,
  channelHasValue,
  emptyMarketplacePair,
  figuresFromPair,
  filterFlipkartAccountsForBrand,
  flipkartPaidPair,
  pairAcos,
  pairFromOrganicPaidView,
  pairHasValue,
  pairTacos,
  rowMatchesMarketplace,
  sliceTotal,
  type AmazonCurrencyView,
  type ChannelSlice,
  type ConsolidatedAsinRow,
  type FlipkartAccountRef,
  type MarketplaceFilter,
  type MarketplacePair,
  type PerformanceFilter,
} from '@/api/consolidatedMetrics';
import { downloadTextFile, reportDateRangeFilenamePart, rowsToCsv } from '@/api/csvExport';
import { dashboardPdfFilename, downloadDashboardPdf } from '@/api/dashboardPdfExport';
import {
  formatSnapshotCurrency,
  formatSnapshotNumber,
  formatSnapshotPercent,
  type OrganicPaidPeriodView,
} from '@/api/snapshotMetrics';
import { selectableFlipkartAccounts } from '@/api/workspaceScope';

type MetricKind = 'sales' | 'orders' | 'gvs' | 'aov' | 'cr' | 'tacos';

const MARKETPLACES: Array<{ id: MarketplaceFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'amazon', label: 'Amazon' },
  { id: 'flipkart', label: 'Flipkart' },
];

const PERFORMANCES: Array<{ id: PerformanceFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'organic', label: 'Organic' },
];

type AdvertisedLite = {
  asin: string;
  productName: string;
  clicks: number;
  cost: number;
  purchases30d: number;
  sales30d: number;
};

type RetailAsinPayload = {
  mapped: boolean;
  sales: SpApiSalesAsinRow[];
  traffic: SpApiTrafficRow[];
  titles: SpApiAsinRow[];
};

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toUpperCase();
}

function parseAdvertisedPage(data: unknown): {
  rows: AdvertisedLite[];
  totalCount: number;
  skus: Map<string, string>;
} {
  const skus = new Map<string, string>();
  if (!data || typeof data !== 'object') return { rows: [], totalCount: 0, skus };
  const raw = data as Record<string, unknown>;
  const items = Array.isArray(raw.rows) ? raw.rows : [];
  const rows: AdvertisedLite[] = [];
  for (const item of items) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const asin = typeof row.asin === 'string' ? row.asin.trim() : '';
    if (!asin) continue;
    const metrics =
      row.adMetrics && typeof row.adMetrics === 'object'
        ? (row.adMetrics as Record<string, unknown>)
        : {};
    const name = typeof row.productName === 'string' ? row.productName.trim() : '';
    rows.push({
      asin,
      productName: name || asin,
      clicks: num(metrics.clicks),
      cost: num(metrics.cost),
      purchases30d: num(metrics.purchases30d),
      sales30d: num(metrics.sales30d),
    });
  }
  const catalog = Array.isArray(raw.catalog) ? raw.catalog : [];
  for (const item of catalog) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const asin = typeof row.asin === 'string' ? row.asin.trim() : '';
    const sku = typeof row.sku === 'string' ? row.sku.trim() : '';
    if (asin && sku) skus.set(norm(asin), sku);
  }
  const totalCount = typeof raw.totalCount === 'number' ? raw.totalCount : rows.length;
  return { rows, totalCount, skus };
}

async function loadRetailAsinPayload(
  profileId: string,
  startDate: string,
  endDate: string
): Promise<RetailAsinPayload> {
  const resolved = await spApiOauthApi.resolveConnection({ profileId });
  if (!resolved.success || !resolved.data) throw new Error('Failed to resolve Vendor connection');
  const listed = resolved.data.connections ?? [];
  const connections = listed.length > 0 ? listed : resolved.data.connection ? [resolved.data.connection] : [];
  if (connections.length === 0) return { mapped: false, sales: [], traffic: [], titles: [] };
  const clipped = clipRangeToVendorLag2(startDate, endDate);
  if (clipped.startDate > clipped.endDate) return { mapped: true, sales: [], traffic: [], titles: [] };
  const params = { start: clipped.startDate, end: clipped.endDate };
  const [salesGroups, trafficGroups, catalogs] = await Promise.all([
    Promise.all(
      connections.map(async (connection) => {
        const res = await spApiOauthApi.getSales(connection.id, params);
        if (!res.success || !res.data) throw new Error('Failed to load retail sales');
        return res.data.sales_by_asin ?? [];
      })
    ),
    Promise.all(
      connections.map(async (connection) => {
        const res = await spApiOauthApi.getTraffic(connection.id, params);
        if (!res.success || !res.data) throw new Error('Failed to load glance views');
        return res.data.rows ?? [];
      })
    ),
    Promise.all(
      connections.map(async (connection) => {
        const res = await spApiOauthApi.getAsins(connection.id);
        if (!res.success || !res.data) return [] as SpApiAsinRow[];
        return res.data.asins ?? [];
      })
    ),
  ]);
  return {
    mapped: true,
    sales: salesGroups.flat(),
    traffic: trafficGroups.flat(),
    titles: catalogs.flat(),
  };
}

function mergeAdvertised(
  results: Array<{ rows: AdvertisedLite[]; skus: Map<string, string> } | undefined>
): { rows: AdvertisedLite[]; skus: Map<string, string> } {
  const rows: AdvertisedLite[] = [];
  const skus = new Map<string, string>();
  for (const result of results) {
    if (!result) continue;
    rows.push(...result.rows);
    for (const [asin, sku] of result.skus) {
      if (!skus.has(asin)) skus.set(asin, sku);
    }
  }
  return { rows, skus };
}

function mergeRetailPayloads(payloads: Array<RetailAsinPayload | undefined>): RetailAsinPayload {
  const present = payloads.filter((payload): payload is RetailAsinPayload => Boolean(payload));
  return {
    mapped: present.some((payload) => payload.mapped),
    sales: present.flatMap((payload) => payload.sales),
    traffic: present.flatMap((payload) => payload.traffic),
    titles: present.flatMap((payload) => payload.titles),
  };
}

async function loadAdvertisedProducts(profileId: string, startDate: string, endDate: string) {
  const pageSize = 100;
  const maxRows = 2000;
  const rows: AdvertisedLite[] = [];
  const skus = new Map<string, string>();
  let offset = 0;
  let totalCount = 0;
  while (offset < maxRows) {
    const res = await adsApi.getWorkspaceAdvertisedProductsPage(profileId, {
      limit: pageSize,
      offset,
      startDate,
      endDate,
    });
    if (!res.success) throw new Error('Failed to load advertised products');
    const parsed = parseAdvertisedPage(res.data);
    totalCount = parsed.totalCount;
    rows.push(...parsed.rows);
    for (const [asin, sku] of parsed.skus) {
      if (!skus.has(asin)) skus.set(asin, sku);
    }
    offset += parsed.rows.length;
    if (parsed.rows.length < pageSize || offset >= totalCount) break;
  }
  return { rows, skus, truncated: offset < totalCount };
}

function formatMetric(kind: MetricKind, value: number | null): string {
  if (value == null || !Number.isFinite(value)) return '—';
  if (kind === 'sales' || kind === 'aov') return formatSnapshotCurrency(value);
  if (kind === 'cr' || kind === 'tacos') return formatSnapshotPercent(value);
  return formatSnapshotNumber(value, 0);
}

function SegmentedFilter<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ id: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <div className="flex items-center gap-1.5" role="group" aria-label={label}>
      <span className="text-body-sm text-foreground-muted">{label}</span>
      <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 shadow-sm">
        {options.map((option) => {
          const selected = option.id === value;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(option.id)}
              className={cn(
                'h-7 rounded-md px-2.5 text-xs font-medium transition-colors',
                selected
                  ? 'bg-brand text-brand-foreground'
                  : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function marketplaceAmount(pair: MarketplacePair, kind: MetricKind): number | null {
  if (kind === 'sales') return sliceTotal(pair, 'sales');
  if (kind === 'orders') return sliceTotal(pair, 'orders');
  if (kind === 'gvs') return sliceTotal(pair, 'gvs');
  if (kind === 'tacos') return pairTacos(pair) ?? pairAcos(pair);
  return figuresFromPair(pair)[kind];
}

function MarketplaceMark({ place }: { place: 'amazon' | 'flipkart' }) {
  return (
    <img
      src={place === 'amazon' ? '/amazon.svg' : '/flipkart.svg'}
      alt=""
      aria-hidden
      className="h-4 w-4 shrink-0 object-contain"
    />
  );
}

function PaidOrganicFooter({
  pair,
  kind,
  performance,
}: {
  pair: MarketplacePair;
  kind: MetricKind;
  performance: PerformanceFilter;
}) {
  const showPaid = performance !== 'organic';
  const showOrganic = performance !== 'paid';

  const valueFor = (slice: ChannelSlice, channel: 'paid' | 'organic'): number | null => {
    if (kind === 'sales') return slice.sales;
    if (kind === 'orders') return slice.orders;
    if (kind === 'gvs') return slice.gvs;
    if (kind === 'aov') return channelAov(slice);
    if (kind === 'cr') return channelCr(slice);
    if (channel === 'paid') return pairAcos(pair) ?? channelAcos(slice);
    return null;
  };

  if (!showPaid && !showOrganic) return null;

  return (
    <div className="mt-2.5 grid grid-cols-2 gap-2 border-t border-border/70 pt-2.5">
      {showPaid ? (
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-brand">Paid</p>
          <p className="mt-0.5 text-xs tabular-nums text-foreground">
            {formatMetric(kind === 'tacos' ? 'tacos' : kind, valueFor(pair.paid, 'paid'))}
          </p>
        </div>
      ) : (
        <div />
      )}
      {showOrganic ? (
        <div className="min-w-0">
          <p className="text-[11px] font-medium text-success">Organic</p>
          <p className="mt-0.5 text-xs tabular-nums text-foreground">
            {formatMetric(kind === 'tacos' ? 'tacos' : kind, valueFor(pair.organic, 'organic'))}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function MarketplaceMetricHalf({
  place,
  kind,
  pair,
  previous,
  compareEnabled,
  performance,
  bordered,
  label,
}: {
  place: 'amazon' | 'flipkart';
  kind: MetricKind;
  pair: MarketplacePair;
  previous: MarketplacePair;
  compareEnabled: boolean;
  performance: PerformanceFilter;
  bordered?: boolean;
  label?: string;
}) {
  const hasData = pairHasValue(pair);
  const current = marketplaceAmount(pair, kind);
  const prev = marketplaceAmount(previous, kind);
  const polarity = kind === 'tacos' ? 'lower_is_better' : 'higher_is_better';
  const placeLabel = label ?? (place === 'amazon' ? 'Amazon' : 'Flipkart');

  return (
    <div className={cn('min-w-0 px-3.5 py-3', bordered && 'border-l border-border')}>
      <div className="flex items-center gap-1.5">
        <MarketplaceMark place={place} />
        <span className="text-xs font-semibold text-foreground">{placeLabel}</span>
      </div>
      {!hasData ? (
        <p className="mt-2 text-xs text-foreground-muted">—</p>
      ) : (
        <>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <p className="text-lg font-bold tabular-nums tracking-tight text-foreground">
              {formatMetric(kind, current)}
            </p>
            {compareEnabled && current != null && prev != null ? (
              <SnapshotChangePill current={current} previous={prev} polarity={polarity} />
            ) : null}
          </div>
          {compareEnabled ? (
            <p className="mt-0.5 text-[11px] text-foreground-muted">
              Prev: <span className="tabular-nums">{formatMetric(kind, prev)}</span>
            </p>
          ) : null}
          <PaidOrganicFooter pair={pair} kind={kind} performance={performance} />
        </>
      )}
    </div>
  );
}

const SUMMARY_CARDS: Array<{ kind: MetricKind; title: string }> = [
  { kind: 'sales', title: 'Total Sales' },
  { kind: 'orders', title: 'Total Orders' },
  { kind: 'gvs', title: 'GVs' },
  { kind: 'aov', title: 'Ad AOV' },
  { kind: 'cr', title: 'Total CR%' },
  { kind: 'tacos', title: 'TACOS/ACOS' },
];

function ConsolidatedMetricCards({
  amazon,
  flipkart,
  previousAmazon,
  previousFlipkart,
  amazonColumns,
  marketplace,
  performance,
  compareEnabled,
}: {
  amazon: MarketplacePair;
  flipkart: MarketplacePair;
  previousAmazon: MarketplacePair;
  previousFlipkart: MarketplacePair;
  amazonColumns?: Array<{ label: string; pair: MarketplacePair; previous: MarketplacePair }> | null;
  marketplace: MarketplaceFilter;
  performance: PerformanceFilter;
  compareEnabled: boolean;
}) {
  const showAmazon = marketplace !== 'flipkart';
  const showFlipkart = marketplace !== 'amazon';
  const columns =
    showAmazon && amazonColumns && amazonColumns.length > 1
      ? amazonColumns.map((column) => ({
          label: column.label,
          pair: applyPerformance(column.pair, performance),
          previous: applyPerformance(column.previous, performance),
        }))
      : null;
  const amazonView = applyPerformance(amazon, performance);
  const flipkartView = applyPerformance(flipkart, performance);
  const previousAmazonView = applyPerformance(previousAmazon, performance);
  const previousFlipkartView = applyPerformance(previousFlipkart, performance);
  const amazonCount = columns ? columns.length : showAmazon ? 1 : 0;
  const columnCount = amazonCount + (showFlipkart ? 1 : 0);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      {SUMMARY_CARDS.map((card) => (
        <Card key={card.kind} className="overflow-hidden p-0 shadow-sm">
          <div className="px-3.5 pt-3">
            <h3 className="text-sm font-semibold text-foreground">{card.title}</h3>
          </div>
          <div
            className="grid"
            style={{ gridTemplateColumns: `repeat(${Math.max(columnCount, 1)}, minmax(0, 1fr))` }}
          >
            {columns
              ? columns.map((column, index) => (
                  <MarketplaceMetricHalf
                    key={column.label}
                    place="amazon"
                    label={column.label}
                    kind={card.kind}
                    pair={column.pair}
                    previous={column.previous}
                    compareEnabled={compareEnabled}
                    performance={performance}
                    bordered={index > 0}
                  />
                ))
              : showAmazon ? (
                  <MarketplaceMetricHalf
                    place="amazon"
                    kind={card.kind}
                    pair={amazonView}
                    previous={previousAmazonView}
                    compareEnabled={compareEnabled}
                    performance={performance}
                  />
                ) : null}
            {showFlipkart ? (
              <MarketplaceMetricHalf
                place="flipkart"
                kind={card.kind}
                pair={flipkartView}
                previous={previousFlipkartView}
                compareEnabled={compareEnabled}
                performance={performance}
                bordered={showAmazon}
              />
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

type AsinMetricCol =
  | 'sales'
  | 'orders'
  | 'gvs'
  | 'paidSales'
  | 'organicSales'
  | 'acos'
  | 'cr';

const ASIN_METRIC_COLS: Array<{ id: AsinMetricCol; label: string }> = [
  { id: 'sales', label: 'Sales' },
  { id: 'orders', label: 'Orders' },
  { id: 'gvs', label: 'GVs' },
  { id: 'paidSales', label: 'Paid Sales' },
  { id: 'organicSales', label: 'Organic Sales' },
  { id: 'acos', label: 'ACOS' },
  { id: 'cr', label: 'CR%' },
];

const DEFAULT_ASIN_COLS: AsinMetricCol[] = ASIN_METRIC_COLS.map((col) => col.id);

function formatAsinCell(
  pair: MarketplacePair,
  col: AsinMetricCol,
  empty: boolean
): string {
  if (empty || !pairHasValue(pair)) return '—';
  if (col === 'sales') return formatMetric('sales', sliceTotal(pair, 'sales'));
  if (col === 'orders') return formatMetric('orders', sliceTotal(pair, 'orders'));
  if (col === 'gvs') return formatMetric('gvs', sliceTotal(pair, 'gvs'));
  if (col === 'paidSales') return formatMetric('sales', pair.paid.sales);
  if (col === 'organicSales') return formatMetric('sales', pair.organic.sales);
  if (col === 'acos') return formatMetric('tacos', pairAcos(pair) ?? channelAcos(pair.paid));
  const total: ChannelSlice = {
    sales: sliceTotal(pair, 'sales'),
    orders: sliceTotal(pair, 'orders'),
    gvs: sliceTotal(pair, 'gvs'),
    clicks: sliceTotal(pair, 'gvs'),
    spend: pair.paid.spend,
  };
  return formatMetric('cr', channelHasValue(total) ? channelCr(total) : null);
}

function asinCellTone(col: AsinMetricCol, role: 'header' | 'body' = 'body'): string {
  if (col === 'paidSales') {
    return role === 'header' ? 'bg-[#E8F1FB]' : 'bg-[#F3F8FD]';
  }
  if (col === 'organicSales') {
    return role === 'header' ? 'bg-[#E8F5EC]' : 'bg-[#F3FAF5]';
  }
  return role === 'header' ? 'bg-surface-header' : '';
}

const FROZEN_ASIN_COLS = ['asin', 'product'] as const;
const ASIN_COL_MIN_PX = 112;
const PRODUCT_COL_MIN_PX = 140;
const PRODUCT_COL_MAX_PX = 160;
const METRIC_COL_MIN_PX = Math.max(WORKSPACE_MIN_COLUMN_WIDTH_PX, 104);
const WIDE_METRIC_COL_MIN_PX = 136;
/** Height of the Amazon/Flipkart group header row — metric titles stick below it. */
const MARKETPLACE_GROUP_STICKY_TOP = '2.25rem';

function metricColWidthPx(col: AsinMetricCol): number {
  return col === 'paidSales' || col === 'organicSales' ? WIDE_METRIC_COL_MIN_PX : METRIC_COL_MIN_PX;
}

function AsinPerformanceTable({
  rows,
  marketplace,
  performance,
  search,
  onSearchChange,
  selectedCols,
  onColsChange,
  exportRangeLabel,
  headerLeft,
}: {
  rows: ConsolidatedAsinRow[];
  marketplace: MarketplaceFilter;
  performance: PerformanceFilter;
  search: string;
  onSearchChange: (value: string) => void;
  selectedCols: AsinMetricCol[];
  onColsChange: (next: AsinMetricCol[]) => void;
  exportRangeLabel: string;
  headerLeft: ReactNode;
}) {
  const showAmazon = marketplace !== 'flipkart';
  const showFlipkart = marketplace !== 'amazon';
  const q = search.trim().toLowerCase();

  const visibleCols = useMemo(() => {
    return selectedCols.filter((col) => {
      if (performance === 'paid' && col === 'organicSales') return false;
      if (performance === 'organic' && col === 'paidSales') return false;
      return true;
    });
  }, [performance, selectedCols]);

  const columnSignature = useMemo(
    () =>
      [
        'asin',
        'product',
        showAmazon ? `amazon:${visibleCols.join(',')}` : '',
        showFlipkart ? `flipkart:${visibleCols.join(',')}` : '',
      ]
        .filter(Boolean)
        .join('|'),
    [showAmazon, showFlipkart, visibleCols]
  );

  const freezeLayout = useWorkspaceFrozenLayout(FROZEN_ASIN_COLS, columnSignature);

  const visible = useMemo(() => {
    return rows
      .filter((row) => rowMatchesMarketplace(row, marketplace))
      .filter((row) => {
        if (!q) return true;
        return (
          row.id.toLowerCase().includes(q) || row.productName.toLowerCase().includes(q)
        );
      });
  }, [marketplace, q, rows]);

  const handleExport = useCallback(() => {
    const places = [
      ...(showAmazon ? (['amazon'] as const) : []),
      ...(showFlipkart ? (['flipkart'] as const) : []),
    ];
    const headers = [
      'ASIN',
      'Product',
      ...places.flatMap((place) =>
        visibleCols.map((col) => {
          const placeLabel = place === 'amazon' ? 'Amazon' : 'Flipkart';
          const colLabel = ASIN_METRIC_COLS.find((c) => c.id === col)?.label ?? col;
          return `${placeLabel} ${colLabel}`;
        })
      ),
    ];
    const csvRows = visible.map((row) => {
      const amazon = applyPerformance(row.amazon, performance);
      const flipkart = applyPerformance(row.flipkart, performance);
      const values: Array<string | number | null> = [row.id, row.productName];
      for (const place of places) {
        const pair = place === 'amazon' ? amazon : flipkart;
        const empty = !pairHasValue(pair);
        for (const col of visibleCols) {
          values.push(formatAsinCell(pair, col, empty));
        }
      }
      return values;
    });
    downloadTextFile(
      `consolidated-asin-performance_${exportRangeLabel}.csv`,
      rowsToCsv(headers, csvRows)
    );
  }, [exportRangeLabel, performance, showAmazon, showFlipkart, visible, visibleCols]);

  const asinStickyHeader = workspaceColumnStickyProps(
    'asin',
    FROZEN_ASIN_COLS,
    freezeLayout.frozenLeftById,
    'header'
  );
  const productStickyHeader = workspaceColumnStickyProps(
    'product',
    FROZEN_ASIN_COLS,
    freezeLayout.frozenLeftById,
    'header'
  );
  const asinStickyBody = workspaceColumnStickyProps(
    'asin',
    FROZEN_ASIN_COLS,
    freezeLayout.frozenLeftById,
    'body'
  );
  const productStickyBody = workspaceColumnStickyProps(
    'product',
    FROZEN_ASIN_COLS,
    freezeLayout.frozenLeftById,
    'body'
  );

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3" data-pdf-ignore>
        <div className="min-w-0">{headerLeft}</div>
        <div className="flex shrink-0 flex-nowrap items-center gap-2">
          <TableSearchInput
            value={search}
            onChange={onSearchChange}
            placeholder="Search ASIN, product name..."
            className="w-56 shrink-0 sm:w-64"
            id="consolidated-asin-search"
          />
          <div className="shrink-0">
            <ReportColumnPicker
              columns={ASIN_METRIC_COLS}
              selected={selectedCols}
              onChange={onColsChange}
              requiredColumnIds={['sales'] as const}
              panelTitle="Metric columns"
            />
          </div>
          <div className="shrink-0">
            <WorkspaceExportButton onExport={handleExport} disabled={visible.length === 0} />
          </div>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-xl border border-border bg-surface px-4 py-12 text-center text-sm text-foreground-muted">
          No ASIN performance for this period.
        </div>
      ) : (
        <div className="max-w-full min-w-0 overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
          <div className="custom-scrollbar max-h-[min(45rem,calc(100vh-14rem))] max-w-full min-w-0 overflow-x-auto overflow-y-auto">
            <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
              <thead>
                <tr>
                  <th
                    rowSpan={2}
                    data-workspace-col-id="asin"
                    className={cn(workspaceTableClass.th, 'align-bottom', asinStickyHeader.className)}
                    style={{
                      ...asinStickyHeader.style,
                      minWidth: ASIN_COL_MIN_PX,
                      width: ASIN_COL_MIN_PX,
                    }}
                  >
                    ASIN
                  </th>
                  <th
                    rowSpan={2}
                    data-workspace-col-id="product"
                    className={cn(workspaceTableClass.th, 'align-bottom', productStickyHeader.className)}
                    style={{
                      ...productStickyHeader.style,
                      minWidth: PRODUCT_COL_MIN_PX,
                      width: PRODUCT_COL_MIN_PX,
                      maxWidth: PRODUCT_COL_MAX_PX,
                    }}
                  >
                    Product
                  </th>
                  {showAmazon ? (
                    <td
                      colSpan={visibleCols.length}
                      className="sticky top-0 z-[36] border-b border-l border-border bg-[#EEF3FB] px-3 py-2 text-center"
                    >
                      <span className="inline-flex items-center justify-center gap-1.5">
                        <MarketplaceMark place="amazon" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
                          Amazon
                        </span>
                      </span>
                    </td>
                  ) : null}
                  {showFlipkart ? (
                    <td
                      colSpan={visibleCols.length}
                      className="sticky top-0 z-[36] border-b border-l border-border bg-[#FFF8E7] px-3 py-2 text-center"
                    >
                      <span className="inline-flex items-center justify-center gap-1.5">
                        <MarketplaceMark place="flipkart" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-foreground">
                          Flipkart
                        </span>
                      </span>
                    </td>
                  ) : null}
                </tr>
                <tr>
                  {showAmazon
                    ? visibleCols.map((col) => (
                        <th
                          key={`amazon-${col}`}
                          data-workspace-col-id={`amazon-${col}`}
                          className={cn(
                            workspaceTableClass.th,
                            'z-[34] overflow-hidden border-l border-border/60 text-ellipsis',
                            asinCellTone(col, 'header'),
                            col === 'paidSales' && 'text-brand',
                            col === 'organicSales' && 'text-success'
                          )}
                          style={{
                            top: MARKETPLACE_GROUP_STICKY_TOP,
                            minWidth: metricColWidthPx(col),
                            width: metricColWidthPx(col),
                          }}
                          title={ASIN_METRIC_COLS.find((c) => c.id === col)?.label}
                        >
                          {ASIN_METRIC_COLS.find((c) => c.id === col)?.label}
                        </th>
                      ))
                    : null}
                  {showFlipkart
                    ? visibleCols.map((col) => (
                        <th
                          key={`flipkart-${col}`}
                          data-workspace-col-id={`flipkart-${col}`}
                          className={cn(
                            workspaceTableClass.th,
                            'z-[34] overflow-hidden border-l border-border/60 text-ellipsis',
                            col === 'paidSales' || col === 'organicSales'
                              ? asinCellTone(col, 'header')
                              : 'bg-[#FFF8E7]',
                            col === 'paidSales' && 'text-brand',
                            col === 'organicSales' && 'text-success'
                          )}
                          style={{
                            top: MARKETPLACE_GROUP_STICKY_TOP,
                            minWidth: metricColWidthPx(col),
                            width: metricColWidthPx(col),
                          }}
                          title={ASIN_METRIC_COLS.find((c) => c.id === col)?.label}
                        >
                          {ASIN_METRIC_COLS.find((c) => c.id === col)?.label}
                        </th>
                      ))
                    : null}
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const amazon = applyPerformance(row.amazon, performance);
                  const flipkart = applyPerformance(row.flipkart, performance);
                  const amazonEmpty = !pairHasValue(amazon);
                  const flipkartEmpty = !pairHasValue(flipkart);
                  return (
                    <tr key={row.id} className={workspaceTableClass.rowHover}>
                      <td
                        className={cn(
                          workspaceTableClass.td,
                          'font-mono text-xs font-medium',
                          asinStickyBody.className
                        )}
                        style={{ ...asinStickyBody.style, minWidth: ASIN_COL_MIN_PX }}
                      >
                        {row.id}
                      </td>
                      <td
                        className={cn(workspaceTableClass.td, 'text-sm', productStickyBody.className)}
                        style={{
                          ...productStickyBody.style,
                          minWidth: PRODUCT_COL_MIN_PX,
                          maxWidth: PRODUCT_COL_MAX_PX,
                        }}
                        title={row.productName}
                      >
                        {row.productName}
                      </td>
                      {showAmazon
                        ? visibleCols.map((col) => (
                            <td
                              key={`${row.id}-amazon-${col}`}
                              className={cn(
                                workspaceTableClass.td,
                                'border-l border-border/40 text-right tabular-nums text-sm',
                                asinCellTone(col, 'body')
                              )}
                              style={{ minWidth: metricColWidthPx(col) }}
                            >
                              {formatAsinCell(amazon, col, amazonEmpty)}
                            </td>
                          ))
                        : null}
                      {showFlipkart
                        ? visibleCols.map((col) => (
                            <td
                              key={`${row.id}-flipkart-${col}`}
                              className={cn(
                                workspaceTableClass.td,
                                'border-l border-border/40 text-right tabular-nums text-sm',
                                col === 'paidSales' || col === 'organicSales'
                                  ? asinCellTone(col, 'body')
                                  : 'bg-[#FFFDF6]'
                              )}
                              style={{ minWidth: metricColWidthPx(col) }}
                            >
                              {formatAsinCell(flipkart, col, flipkartEmpty)}
                            </td>
                          ))
                        : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function ConsolidatedDashboard({
  profileId,
  isDemo,
  range,
  currentView,
  previousView,
  snapshotLoading,
  snapshotError,
  onRetry,
  truncated: _truncated,
  retailDataThrough: _retailDataThrough,
  rangeEnd,
  brandAmazonProfiles = null,
  flipkartAccountFilter = null,
  amazonCurrencyViews = null,
}: {
  profileId: string;
  isDemo: boolean;
  range: SnapshotRangeState;
  currentView: OrganicPaidPeriodView | null;
  previousView: OrganicPaidPeriodView | null;
  snapshotLoading: boolean;
  snapshotError: Error | null;
  onRetry: () => void;
  truncated: boolean;
  retailDataThrough: string | null;
  rangeEnd: string;
  /** Set on the brand dashboard. Empty means the brand has no Amazon Ads accounts. */
  brandAmazonProfiles?: Array<{ profileId: string; currency: string | null }> | null;
  /** Set on the brand dashboard. Null keeps every Flipkart account. */
  flipkartAccountFilter?: FlipkartAccountRef[] | null;
  amazonCurrencyViews?: AmazonCurrencyView[] | null;
}) {
  void _truncated;
  void _retailDataThrough;
  const { showToast } = useToast();
  const exportRootRef = useRef<HTMLDivElement>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [marketplace, setMarketplace] = useState<MarketplaceFilter>('all');
  const [performance, setPerformance] = useState<PerformanceFilter>('all');
  const [asinSearch, setAsinSearch] = useState('');
  const [asinCols, setAsinCols] = useState<AsinMetricCol[]>(DEFAULT_ASIN_COLS);
  const rangesOk = snapshotRangesValid(range);

  const accountsQuery = useQuery({
    queryKey: FLIPKART_ADS_ALL_ACCOUNTS_QUERY_KEY,
    queryFn: async () => {
      const res = await flipkartAdsApi.listAllAccounts();
      if (!res.success) throw new Error('Failed to load Flipkart accounts');
      return res.data.accounts;
    },
    enabled: !isDemo,
    staleTime: 60_000,
  });

  const accounts = useMemo(() => {
    if (isDemo) return [];
    const selectable = selectableFlipkartAccounts(accountsQuery.data ?? []).flatMap((account) =>
      account.platform === 'flipkart' ? [account] : []
    );
    return filterFlipkartAccountsForBrand(selectable, flipkartAccountFilter);
  }, [accountsQuery.data, flipkartAccountFilter, isDemo]);

  const productProfileIds = brandAmazonProfiles
    ? brandAmazonProfiles.map((profile) => profile.profileId)
    : profileId
      ? [profileId]
      : [];

  const reportQueries = useQueries({
    queries: accounts.map((account) => ({
      queryKey: flipkartAdsReportsQueryKey(account.credentialId, 'products', account.accountId),
      queryFn: async () => {
        const res = await flipkartAdsApi.listReports(account.credentialId, 'products', {
          adAccountId: account.accountId,
        });
        if (!res.success) throw new Error('Failed to load Flipkart product reports');
        return res.data.rows;
      },
      staleTime: 60_000,
    })),
  });

  const productQueries = useQueries({
    queries: accounts.map((account) => ({
      queryKey: ['flipkart-ads-products', account.credentialId, account.accountId] as const,
      queryFn: async () => {
        const res = await flipkartAdsApi.listProducts(account.credentialId, {
          adAccountId: account.accountId,
        });
        if (!res.success) throw new Error('Failed to load Flipkart products');
        return res.data.products;
      },
      staleTime: 60_000,
    })),
  });

  const advertisedQueries = useQueries({
    queries: productProfileIds.map((id) => ({
      queryKey: ['consolidated-advertised-products', id, range.current.startDate, range.current.endDate],
      queryFn: () => loadAdvertisedProducts(id, range.current.startDate, range.current.endDate),
      enabled: Boolean(id) && rangesOk,
      staleTime: 60_000,
    })),
  });

  const retailQueries = useQueries({
    queries: productProfileIds.map((id) => ({
      queryKey: ['consolidated-retail-asins', id, range.current.startDate, range.current.endDate],
      queryFn: () => loadRetailAsinPayload(id, range.current.startDate, range.current.endDate),
      enabled: !isDemo && Boolean(id) && rangesOk,
      staleTime: 60_000,
    })),
  });

  const flipkartRows = reportQueries.flatMap((query) => query.data ?? []);
  const flipkartProducts = productQueries.flatMap((query) => query.data ?? []);
  const reportsLoading =
    !isDemo &&
    (accountsQuery.isLoading || reportQueries.some((query) => query.isPending));
  const productsLoading = !isDemo && productQueries.some((query) => query.isPending);
  const cardsLoading = snapshotLoading || reportsLoading;
  const advertisedLoading = advertisedQueries.some((query) => query.isLoading);
  const retailLoading = !isDemo && retailQueries.some((query) => query.isLoading);
  const tableLoading = advertisedLoading || retailLoading || reportsLoading || productsLoading;

  const flipkartCurrent = useMemo(
    () => summarizeFlipkartWindow(flipkartRows, range.current),
    [flipkartRows, range.current]
  );
  const flipkartPrevious = useMemo(
    () => summarizeFlipkartWindow(flipkartRows, range.previous),
    [flipkartRows, range.previous]
  );

  const brandMode = amazonCurrencyViews != null;
  const multiCurrencyColumns =
    brandMode && amazonCurrencyViews.length > 1
      ? amazonCurrencyViews.map((column) => ({
          label: column.currency ? `Amazon ${column.currency}` : 'Amazon',
          pair: column.current ? pairFromOrganicPaidView(column.current) : emptyMarketplacePair(),
          previous: column.previous
            ? pairFromOrganicPaidView(column.previous)
            : emptyMarketplacePair(),
        }))
      : null;
  const brandAmazonView =
    brandMode && amazonCurrencyViews.length <= 1 ? (amazonCurrencyViews[0] ?? null) : null;
  const amazonPair = brandMode
    ? multiCurrencyColumns
      ? multiCurrencyColumns[0].pair
      : brandAmazonView?.current
        ? pairFromOrganicPaidView(brandAmazonView.current)
        : emptyMarketplacePair()
    : currentView
      ? pairFromOrganicPaidView(currentView)
      : null;
  const previousAmazonPair = brandMode
    ? multiCurrencyColumns
      ? multiCurrencyColumns[0].previous
      : brandAmazonView?.previous
        ? pairFromOrganicPaidView(brandAmazonView.previous)
        : emptyMarketplacePair()
    : previousView
      ? pairFromOrganicPaidView(previousView)
      : null;
  const flipkartPair = reportsLoading ? null : flipkartWindowPair(flipkartCurrent.hasRows, flipkartCurrent.metrics);
  const previousFlipkartPair = reportsLoading
    ? null
    : flipkartWindowPair(flipkartPrevious.hasRows, flipkartPrevious.metrics);

  const amazonContentReady = brandMode
    ? amazonCurrencyViews.length === 0 || amazonCurrencyViews.some((column) => column.current)
    : Boolean(currentView);
  const cardsReady = Boolean(amazonPair && flipkartPair) && amazonContentReady;
  const compareEnabled = Boolean(previousAmazonPair && previousFlipkartPair);

  const advertisedByProfile = new Map(
    productProfileIds.map((id, index) => [id, advertisedQueries[index]?.data])
  );
  const retailByProfile = new Map(
    productProfileIds.map((id, index) => [id, retailQueries[index]?.data])
  );
  const retailReadyByProfile = new Map(
    productProfileIds.map((id, index) => [id, !isDemo && Boolean(retailQueries[index]?.isSuccess)])
  );

  const asinRows = useMemo(() => {
    const shared = {
      flipkartRows,
      flipkartProducts,
      currentStart: range.current.startDate,
      currentEnd: range.current.endDate,
      previousStart: range.previous.startDate,
      previousEnd: range.previous.endDate,
    };
    const rowsFor = (profileIds: string[], asinPrefix: string | null, includeFlipkart: boolean) => {
      const advertised = mergeAdvertised(profileIds.map((id) => advertisedByProfile.get(id)));
      const retailPayloads = profileIds.map((id) => retailByProfile.get(id));
      const retailReady = profileIds.some((id) => retailReadyByProfile.get(id));
      return buildRows({
        ...shared,
        advertised: advertised.rows,
        skus: advertised.skus,
        retail: mergeRetailPayloads(retailPayloads),
        retailReady,
        includeFlipkart,
        asinPrefix,
      });
    };
    if (brandMode && amazonCurrencyViews && amazonCurrencyViews.length > 1) {
      return amazonCurrencyViews.flatMap((group, index) =>
        rowsFor(group.profileIds, group.currency || 'OTHER', index === 0)
      );
    }
    return rowsFor(productProfileIds, null, true);
  }, [
    advertisedQueries,
    amazonCurrencyViews,
    brandMode,
    flipkartProducts,
    flipkartRows,
    productProfileIds,
    range.current.endDate,
    range.current.startDate,
    range.previous.endDate,
    range.previous.startDate,
    retailQueries,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    const root = exportRootRef.current;
    if (!root || isExportingPdf || cardsLoading || !cardsReady) return;
    setIsExportingPdf(true);
    try {
      await downloadDashboardPdf(root, {
        filename: dashboardPdfFilename(rangeEnd),
      });
    } catch (err) {
      console.error('Dashboard PDF export failed', err);
      const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
      showToast(`Could not download the dashboard PDF. Please try again.${detail}`, 'error');
    } finally {
      setIsExportingPdf(false);
    }
  }, [cardsLoading, cardsReady, isExportingPdf, rangeEnd, showToast]);

  const exportRangeLabel = reportDateRangeFilenamePart(range.current.startDate, range.current.endDate);

  return (
    <div ref={exportRootRef} className="mx-auto min-h-full max-w-[90rem] space-y-4 bg-page px-4 py-5">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Consolidated View</h1>
          <p className="mt-0.5 text-xs text-foreground-muted">Amazon + Flipkart · ASIN Performance</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SegmentedFilter label="Marketplace" value={marketplace} options={MARKETPLACES} onChange={setMarketplace} />
          <SegmentedFilter label="Performance" value={performance} options={PERFORMANCES} onChange={setPerformance} />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            data-pdf-ignore
            disabled={!cardsReady || cardsLoading || isExportingPdf}
            onClick={() => void handleDownloadPdf()}
            aria-busy={isExportingPdf}
            aria-label={isExportingPdf ? 'Generating PDF' : 'Download dashboard as PDF'}
            className="min-w-[9.5rem] shrink-0 border-brand text-brand hover:bg-brand/5 disabled:opacity-80"
          >
            {isExportingPdf ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
            ) : (
              <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />
            )}
            <span className="whitespace-nowrap">{isExportingPdf ? 'Generating PDF…' : 'Download PDF'}</span>
          </Button>
        </div>
      </div>

      {snapshotError && !amazonContentReady ? (
        <div className="rounded-xl border border-error/20 bg-error-subtle px-4 py-6 text-center">
          <p className="text-sm text-error-ink">{snapshotError.message}</p>
          <button type="button" onClick={onRetry} className="mt-3 text-sm font-semibold text-brand hover:underline">
            Retry
          </button>
        </div>
      ) : null}

      {cardsLoading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-foreground-muted">
          <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
          Loading consolidated view…
        </div>
      ) : null}

      {!cardsLoading && !cardsReady && !snapshotError ? (
        <div className="rounded-xl border border-border bg-surface px-4 py-10 text-center">
          <p className="text-sm text-foreground-muted">No performance data for this period.</p>
        </div>
      ) : null}

      {!cardsLoading && cardsReady && amazonPair && flipkartPair ? (
        <>
          <ConsolidatedMetricCards
            amazon={amazonPair}
            flipkart={flipkartPair}
            previousAmazon={previousAmazonPair ?? emptyMarketplacePair()}
            previousFlipkart={previousFlipkartPair ?? emptyMarketplacePair()}
            amazonColumns={multiCurrencyColumns}
            marketplace={marketplace}
            performance={performance}
            compareEnabled={compareEnabled}
          />

          {tableLoading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-foreground-muted">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              Loading ASIN performance…
            </div>
          ) : (
            <AsinPerformanceTable
              rows={asinRows}
              marketplace={marketplace}
              performance={performance}
              search={asinSearch}
              onSearchChange={setAsinSearch}
              selectedCols={asinCols}
              onColsChange={setAsinCols}
              exportRangeLabel={exportRangeLabel}
              headerLeft={
                <>
                  <h2 className="text-sm font-semibold text-foreground">ASIN Performance</h2>
                  <p className="mt-0.5 text-xs text-foreground-muted">
                    Compare ASIN-level performance across Amazon and Flipkart
                  </p>
                </>
              }
            />
          )}
        </>
      ) : null}
    </div>
  );
}

function flipkartWindowPair(
  hasRows: boolean,
  metrics: { revenue: number; orders: number; clicks: number; spend: number }
): MarketplacePair {
  if (!hasRows) return emptyMarketplacePair();
  return flipkartPaidPair(metrics);
}

function buildRows(args: {
  advertised: AdvertisedLite[];
  skus: Map<string, string>;
  retail: RetailAsinPayload | undefined;
  retailReady: boolean;
  flipkartRows: FlipkartAdsReportRow[];
  flipkartProducts: FlipkartAdsProduct[];
  currentStart: string;
  currentEnd: string;
  previousStart: string;
  previousEnd: string;
  includeFlipkart?: boolean;
  /** Keeps Amazon money from different currencies on separate product rows. */
  asinPrefix?: string | null;
}): ConsolidatedAsinRow[] {
  const includeFlipkart = args.includeFlipkart !== false;
  const prefix = args.asinPrefix?.trim() || '';
  const tag = (asin: string) => (prefix ? `${prefix}:${asin}` : asin);
  const advertised = prefix
    ? args.advertised.map((row) => ({
        ...row,
        asin: tag(row.asin),
        productName: row.productName ? `${row.productName} (${prefix})` : row.productName,
      }))
    : args.advertised;
  const skus = prefix
    ? new Map([...args.skus.entries()].map(([asin, sku]) => [tag(asin), sku]))
    : args.skus;
  const retail =
    prefix && args.retail
      ? {
          ...args.retail,
          sales: args.retail.sales.map((row) => ({ ...row, asin: tag(row.asin) })),
          traffic: args.retail.traffic.map((row) => ({ ...row, asin: tag(row.asin) })),
          titles: args.retail.titles.map((row) => ({ ...row, asin: tag(row.asin) })),
        }
      : args.retail;
  const flipkartRows = includeFlipkart ? args.flipkartRows : [];
  const flipkartProducts = includeFlipkart ? args.flipkartProducts : [];
  const scoped = { ...args, advertised, skus, retail, flipkartRows, flipkartProducts };
  const sales = new Map<string, { revenue: number; units: number }>();
  const traffic = new Map<string, number>();
  const titles = new Map<string, string>();
  const mapped = Boolean(scoped.retailReady && scoped.retail?.mapped);
  if (mapped && scoped.retail) {
    for (const row of rollupSalesByAsin(scoped.retail.sales)) {
      const key = norm(row.asin);
      if (!key) continue;
      const current = sales.get(key) ?? { revenue: 0, units: 0 };
      current.revenue += row.orderedRevenue;
      current.units += row.orderedUnits;
      sales.set(key, current);
    }
    for (const row of rollupTrafficByAsin(scoped.retail.traffic)) {
      const key = norm(row.asin);
      if (!key) continue;
      traffic.set(key, (traffic.get(key) ?? 0) + row.glanceViews);
    }
    for (const row of scoped.retail.titles) {
      const key = norm(row.asin);
      const title = row.title?.trim();
      if (key && title) titles.set(key, title);
    }
  }

  const drafts = new Map<
    string,
    {
      asin: string;
      productName: string;
      sku: string;
      ad: { clicks: number; cost: number; purchases30d: number; sales30d: number } | null;
    }
  >();

  const ensure = (asin: string) => {
    const key = norm(asin);
    let draft = drafts.get(key);
    if (!draft) {
      draft = { asin: asin.trim(), productName: titles.get(key) || asin.trim(), sku: scoped.skus.get(key) ?? '', ad: null };
      drafts.set(key, draft);
    }
    return draft;
  };

  for (const row of scoped.advertised) {
    const draft = ensure(row.asin);
    const metrics = draft.ad ?? { clicks: 0, cost: 0, purchases30d: 0, sales30d: 0 };
    metrics.clicks += row.clicks;
    metrics.cost += row.cost;
    metrics.purchases30d += row.purchases30d;
    metrics.sales30d += row.sales30d;
    draft.ad = metrics;
    if (row.productName && row.productName !== row.asin) draft.productName = row.productName;
    if (!draft.sku) draft.sku = scoped.skus.get(norm(row.asin)) ?? '';
  }

  if (mapped) {
    for (const key of new Set([...sales.keys(), ...traffic.keys()])) {
      const draft = ensure(key);
      const title = titles.get(key);
      if (title && (draft.productName === draft.asin || !draft.productName)) draft.productName = title;
    }
  }

  const names = new Map<string, string>();
  for (const product of scoped.flipkartProducts) {
    const name = product.name?.trim();
    if (!name) continue;
    for (const id of [product.fsn, product.product_id, product.sku]) {
      const key = norm(id);
      if (key && !names.has(key)) names.set(key, name);
    }
  }

  const currentRange = { startDate: args.currentStart, endDate: args.currentEnd };
  const previousRange = { startDate: args.previousStart, endDate: args.previousEnd };
  const currentKeys = new Set<string>();
  for (const row of scoped.flipkartRows) {
    if (!dateInRange(row.date, currentRange)) continue;
    const key = norm(row.fsn) || norm(row.product_id);
    if (key) currentKeys.add(key);
  }

  const groups = groupFlipkartSynopsisRows(scoped.flipkartRows, {
    current: currentRange,
    previous: previousRange,
    keyOf: (row) => row.fsn?.trim() || row.product_id?.trim() || null,
    secondaryOf: (row, key) => names.get(norm(row.fsn)) || names.get(norm(row.product_id)) || names.get(norm(key)) || null,
  });

  const flipkart = groups
    .filter((group) => currentKeys.has(norm(group.key)))
    .map((group) => {
      const key = norm(group.key);
      const aliases = new Set<string>([group.key]);
      for (const product of scoped.flipkartProducts) {
        const ids = [product.fsn, product.product_id, product.sku].map(norm).filter(Boolean);
        if (ids.includes(key)) ids.forEach((id) => aliases.add(id));
      }
      return {
        id: group.key,
        productName: group.secondary || names.get(key) || null,
        aliases: [...aliases],
        metrics: {
          revenue: group.current.revenue,
          orders: group.current.orders,
          clicks: group.current.clicks,
          spend: group.current.spend,
        },
      };
    });

  const amazon = [...drafts.values()]
    .filter((draft) => {
      const key = norm(draft.asin);
      if (draft.ad) return true;
      return mapped && (sales.has(key) || traffic.has(key));
    })
    .map((draft) => {
      const key = norm(draft.asin);
      const retail: WorkspaceRetailKpiMetrics = mapped
        ? {
            totalSales: sales.get(key)?.revenue ?? 0,
            organicSales: null,
            totalOrders: sales.get(key)?.units ?? 0,
            organicOrders: null,
            tacos: null,
            glanceViews: traffic.get(key) ?? 0,
          }
        : EMPTY_RETAIL_KPI_METRICS;
      const title = titles.get(key);
      return {
        asin: draft.asin,
        productName: draft.productName || title || draft.asin,
        sku: draft.sku,
        ad: draft.ad,
        retail,
      };
    });

  return buildConsolidatedAsinRows({ amazon, flipkart });
}
